<template>
  <div class="card h-100 shadow-none border">
    <div
      class="card-header pb-0 d-flex justify-content-between align-items-center"
    >
      <h6 class="mb-0">訂單對話</h6>
      <span class="badge bg-secondary">{{ messages.length }} 則訊息</span>
    </div>
    <div class="card-body p-3 d-flex flex-column" style="height: 500px">
      <!-- Chat Area -->
      <div
        ref="chatContainer"
        class="flex-grow-1 overflow-auto mb-3 p-2 bg-gray-50 rounded"
        style="height: 0px"
      >
        <div v-if="loading && messages.length === 0" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <div
          v-if="!loading && messages.length === 0"
          class="text-center py-5 text-muted text-sm"
        >
          尚無對話記錄
        </div>

        <div
          v-for="msg in messages"
          :key="msg.id"
          class="d-flex mb-3"
          :class="msg.is_self ? 'justify-content-end' : 'justify-content-start'"
        >
          <div
            class="p-2 rounded shadow-sm position-relative"
            :class="
              msg.is_self
                ? 'bg-gradient-primary text-white'
                : 'bg-white text-dark border'
            "
            style="max-width: 80%; min-width: 120px"
          >
            <!-- Header -->
            <div
              class="d-flex justify-content-between align-items-center mb-1 gap-2"
            >
              <small
                class="fw-bold"
                :class="msg.is_self ? 'text-white' : 'text-primary'"
              >
                {{ msg.sender_name }}
              </small>
              <small
                class=""
                :class="msg.is_self ? 'text-white-50' : 'text-muted'"
                style="font-size: 0.7rem"
              >
                {{ formatTime(msg.created_at) }}
              </small>
            </div>

            <!-- Content -->
            <div class="message-content text-sm mb-1">
              <!-- Text -->
              <div v-if="msg.type === 'text'">{{ msg.message }}</div>

              <!-- Image -->
              <div v-if="msg.type === 'image'">
                <img
                  :src="msg.file_url"
                  class="img-fluid rounded mb-1 bg-white"
                  style="max-height: 150px"
                  @click="openImage(msg.file_url)"
                />
              </div>

              <!-- File -->
              <div
                v-if="msg.type === 'file'"
                class="d-flex align-items-center gap-2 p-2 rounded"
                :class="msg.is_self ? 'bg-white-20' : 'bg-light'"
              >
                <i class="fas fa-file-alt"></i>
                <a
                  :href="msg.file_url"
                  target="_blank"
                  class="text-truncate d-block"
                  :class="msg.is_self ? 'text-white' : 'text-dark'"
                  style="max-width: 150px"
                >
                  {{ msg.original_filename || "附件" }}
                </a>
              </div>
            </div>

            <!-- Actions (Only for self, within 24h) -->
            <div
              v-if="msg.can_edit"
              class="position-absolute top-0 start-100 translate-middle-x mt-1 ms-2 dropdown"
            >
              <button
                class="btn btn-link btn-action p-0 text-secondary"
                data-bs-toggle="dropdown"
              >
                <i class="fas fa-ellipsis-v text-xs"></i>
              </button>
              <ul class="dropdown-menu">
                <li v-if="msg.type === 'text'">
                  <a
                    class="dropdown-item"
                    href="#"
                    @click.prevent="startEdit(msg)"
                    >編輯</a
                  >
                </li>
                <li>
                  <a
                    class="dropdown-item text-danger"
                    href="#"
                    @click.prevent="deleteMessage(msg.id)"
                    >刪除 (收回)</a
                  >
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Input Area -->
      <div class="border-top pt-2">
        <!-- File Preview -->
        <div
          v-if="file"
          class="start-section d-flex align-items-center gap-2 mb-2 p-2 bg-light rounded"
        >
          <i class="fas fa-paperclip"></i>
          <small class="text-truncate flex-grow-1">{{ file.name }}</small>
          <button class="btn btn-link p-0 text-danger" @click="file = null">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Edit Mode Warning -->
        <div
          v-if="editingMessage"
          class="d-flex justify-content-between align-items-center mb-2 px-2 py-1 bg-warning-subtle text-warning-emphasis rounded text-xs"
        >
          <span><i class="fas fa-pen me-1"></i> 編輯訊息中...</span>
          <button class="btn btn-link p-0 text-muted" @click="cancelEdit">
            取消
          </button>
        </div>

        <div class="d-flex gap-2">
          <button
            class="btn btn-outline-secondary mb-0 px-3"
            @click="$refs.fileInput.click()"
          >
            <i class="fas fa-paperclip"></i>
          </button>
          <input
            type="file"
            ref="fileInput"
            class="d-none"
            @change="handleFileSelect"
          />

          <textarea
            v-model="newMessage"
            class="form-control"
            rows="1"
            placeholder="輸入訊息..."
            @keydown.enter.exact.prevent="sendMessage"
          ></textarea>

          <button
            class="btn btn-primary mb-0 px-3"
            @click="sendMessage"
            :disabled="sending"
          >
            <i
              class="fas"
              :class="sending ? 'fa-spinner fa-spin' : 'fa-paper-plane'"
            ></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from "@/utils/request";
import echo from "@/utils/echo";
import Swal from "sweetalert2";

export default {
  name: "OrderChat",
  props: {
    orderId: {
      type: [Number, String],
      required: true,
    },
    order: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      messages: [],
      loading: false,
      sending: false,
      newMessage: "",
      file: null,
      timer: null,
      editingMessage: null,
      currentUser: JSON.parse(localStorage.getItem("user") || "{}"),
    };
  },
  async mounted() {
    await this.fetchMessages(true);

    // Ensure Echo has the latest token
    if (echo.connector.pusher.config.auth) {
      echo.connector.pusher.config.auth.headers.Authorization = `Bearer ${localStorage.getItem(
        "token"
      )}`;
    }

    // WebSocket
    echo
      .private(`orders.${this.orderId}`)
      .listen(".MessageSent", (e) => {
        const newMsg = {
          ...e.message,
          is_self: e.message.user_id === this.currentUser.id,
        };
        this.messages.push(newMsg);
        this.$nextTick(this.scrollToBottom);
      })
      .listen(".MessageWithdrawn", (e) => {
        this.messages = this.messages.filter((m) => m.id !== e.messageId);
      });
  },
  beforeUnmount() {
    echo.leave(`orders.${this.orderId}`);
  },
  methods: {
    formatTime(dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    async fetchMessages(firstLoad = false) {
      if (firstLoad) this.loading = true;
      try {
        const res = await request.get(`/api/admin/orders/${this.orderId}/chat`);
        if (Array.isArray(res.data)) {
          const newMessages = res.data.map((m) => ({
            ...m,
            is_self: m.user_id === this.currentUser.id, // Robust check
          }));
          if (newMessages.length > this.messages.length) {
            this.$nextTick(this.scrollToBottom);
          }
          this.messages = newMessages;
        } else if (res.data.success && res.data.data) {
          const newMessages = res.data.data.map((m) => ({
            ...m,
            is_self: m.user_id === this.currentUser.id,
          }));
          this.messages = newMessages;
        }
      } catch (e) {
        // console.error("Fetch chat failed", e);
      } finally {
        if (firstLoad) this.loading = false;
      }
    },
    scrollToBottom() {
      const container = this.$refs.chatContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    handleFileSelect(e) {
      const files = e.target.files;
      if (files.length > 0) {
        this.file = files[0];
      }
    },
    async sendMessage() {
      if ((!this.newMessage.trim() && !this.file) || this.sending) return;

      this.sending = true;

      // LINE Prompt Logic
      let useLine = false;
      if (
        this.order &&
        this.order.buyer &&
        this.order.buyer.line_id &&
        !this.editingMessage
      ) {
        try {
          // Fetch quota
          const quotaRes = await request.get("/api/admin/orders/line/quota");
          const quota = quotaRes.data;
          const remaining = quota.limit - quota.usage;

          const result = await Swal.fire({
            title: "發送 LINE 通知？",
            html: `用戶已綁定 LINE。<br>本月剩餘額度: <b>${remaining}</b> / ${quota.limit}`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "是, 發送 LINE",
            cancelButtonText: "否, 僅存資料庫",
            reverseButtons: true,
          });

          if (result.isConfirmed) {
            useLine = true;
          }
        } catch (e) {
          console.error("Quota fetch failed", e);
          // Fallback prompt
          const result = await Swal.fire({
            title: "發送 LINE 通知？",
            text: "用戶已綁定 LINE。",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "是",
            cancelButtonText: "否",
          });
          useLine = result.isConfirmed;
        }
      }

      const formData = new FormData();

      if (this.editingMessage) {
        // Update
        if (this.newMessage.trim()) {
          try {
            await request.put(`/api/admin/chat/${this.editingMessage.id}`, {
              message: this.newMessage,
            });
            this.cancelEdit();
            this.fetchMessages();
          } catch (e) {
            Swal.fire("錯誤", "更新失敗", "error");
          }
        }
        this.sending = false;
        return;
      }

      // Create
      formData.append("order_id", this.orderId);
      if (this.newMessage.trim()) formData.append("message", this.newMessage);
      if (this.file) formData.append("file", this.file);
      formData.append("use_line", useLine ? "1" : "0");

      try {
        await request.post(`/api/admin/orders/${this.orderId}/chat`, formData);
        this.newMessage = "";
        this.file = null;
        // this.$refs.fileInput.value = ''; // Reset file input
        await this.fetchMessages();
      } catch (e) {
        // console.error(e);
        Swal.fire("錯誤", e.response?.data?.message || "發送失敗", "error");
      } finally {
        this.sending = false;
      }
    },
    deleteMessage(id) {
      Swal.fire({
        title: "確定要收回此訊息嗎？",
        text: "24小時內的訊息可收回",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "收回",
        cancelButtonText: "取消",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await request.delete(`/api/admin/chat/${id}`);
            this.fetchMessages();
          } catch (e) {
            Swal.fire("錯誤", e.response?.data?.message || "刪除失敗", "error");
          }
        }
      });
    },
    startEdit(msg) {
      this.editingMessage = msg;
      this.newMessage = msg.message;
      this.$refs.chatContainer.focus(); // just to move focus away
    },
    cancelEdit() {
      this.editingMessage = null;
      this.newMessage = "";
    },
    openImage(url) {
      window.open(url, "_blank");
    },
  },
};
</script>

<style scoped>
.btn-action {
  opacity: 0.5;
}
.btn-action:hover {
  opacity: 1;
}
.bg-white-20 {
  background: rgba(255, 255, 255, 0.2);
}
</style>
